import React, { JSX } from 'react';
import { Edit, Visibility } from '@mui/icons-material';
import { Box, Chip, IconButton, TableCell, TableRow, Theme, Typography } from '@mui/material';
import * as Types from 'types/types';
import { DeleteConfirmation } from './DeleteConfirmation';
import { formatFileSize, getFileTypeColor } from './documentListUtils';

interface DocumentListRowProps {
  doc: Types.Document;
  isMobile: boolean;
  selectedDocument?: Types.Document | null;
  theme: Theme;
  onSelect: (document: Types.Document) => void;
  onView: (document: Types.Document, edit?: boolean) => void;
  onDelete: (document: Types.Document) => void;
  onRagToggle: (document: Types.Document, includeInRag: boolean) => void;
}

function FileTypeBadge(props: { doc: Types.Document; theme: Theme }): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        width: 'fit-content',
        flexShrink: 0,
        border: `1px solid ${getFileTypeColor(props.theme, props.doc.type)}`,
        height: 20,
        px: 1,
        fontSize: '0.65rem',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {props.doc.type.toUpperCase()}
    </Box>
  );
}

function DocumentNameCell(props: {
  doc: Types.Document;
  isMobile: boolean;
  theme: Theme;
}): JSX.Element {
  const { doc, isMobile, theme } = props;

  return (
    <TableCell sx={{ py: 1, maxWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
        <Typography
          variant="body2"
          title={doc.filename}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '0.875rem',
            minWidth: 0,
            flex: '1 1 auto',
          }}
        >
          {doc.filename}
        </Typography>
        {isMobile && <FileTypeBadge doc={doc} theme={theme} />}
        {isMobile && (
          <Box sx={{ width: '100%', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {formatFileSize(doc.size)} •{' '}
              {(doc?.updatedAt || doc?.uploadDate)?.toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </Box>
    </TableCell>
  );
}

export function DocumentListRow(props: DocumentListRowProps): JSX.Element {
  const { doc, isMobile, selectedDocument, theme, onSelect, onView, onDelete, onRagToggle } = props;
  const isSelected = selectedDocument?.id === doc.id;

  return (
    <TableRow
      hover
      sx={{
        backgroundColor: isSelected ? 'action.selected' : 'transparent',
        '&:hover': {
          backgroundColor: isSelected
            ? 'rgba(0, 0, 0, 0.25) !important'
            : 'action.hover !important',
        },
        cursor: 'pointer',
      }}
      onClick={() => onSelect(doc)}
    >
      <DocumentNameCell doc={doc} isMobile={isMobile} theme={theme} />
      {!isMobile && (
        <TableCell sx={{ py: 1 }}>
          <FileTypeBadge doc={doc} theme={theme} />
        </TableCell>
      )}
      {!isMobile && (
        <TableCell sx={{ py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(doc.size)}
          </Typography>
        </TableCell>
      )}
      {!isMobile && (
        <TableCell sx={{ py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {(doc?.updatedAt || doc?.uploadDate)?.toLocaleDateString()}
          </Typography>
        </TableCell>
      )}
      <TableCell sx={{ py: 1, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        <Chip
          label="RAG"
          size="small"
          color="success"
          variant={doc.options?.includeInRag ? 'filled' : 'outlined'}
          onClick={e => {
            e.stopPropagation();
            onRagToggle(doc, !doc.options?.includeInRag);
          }}
          sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
        />
      </TableCell>
      <TableCell sx={{ py: 1, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
          <IconButton size="small" onClick={() => onView(doc)} title="View content" sx={{ p: 0.5 }}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onView(doc, true)} title="Rename" sx={{ p: 0.5 }}>
            <Edit fontSize="small" />
          </IconButton>
          <DeleteConfirmation
            onDelete={() => onDelete(doc)}
            sx={{ minWidth: 'auto', maxHeight: 'min-content' }}
            size="small"
            action="delete"
            label="this document"
            title="Delete document"
            message="Are you sure you want to delete this document? This action cannot be undone."
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}
